import React from "react";

import { Moment } from "moment";

import useDateFormat from "commons/hooks/useDateFormat";
import useInputMask from "commons/hooks/useInputMask";

import { CheckErrorVisibilityInterface, SetValueInterface } from "commons/types/form";

import moment from "commons/utils/moment";

type ByPassParamsType = {
    value?: number | null;
    label?: string;
    toolbarTitle?: string;
    minDate?: Moment;
    maxDate?: Moment;
    disabled?: boolean;
    filterInput?: boolean;
    required?: boolean;
    tabIndex?: number;
    inputTextExtraClassName?: string;
    startAdornment?: JSX.Element;
    autoFocus?: boolean;
};

export type HookParamsType = ByPassParamsType & {
    onChange?: (value: number | null) => void;
    setValue?: SetValueInterface;
    checkErrorVisibility?: CheckErrorVisibilityInterface;
    path?: string;
    onFocus?: () => void;
    onBlur?: () => void;
};

export type DatePickerHook = ByPassParamsType & {
    isOpen: boolean;
    errors: Array<string>;
    dateString: string;
    onOpen: () => void;
    onClose: () => void;
    onTodayIconClick: () => void;
    onInputTextChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onDatePickerChange: (date: Moment | null) => void;
    clearPicker: () => void;
    onFocus?: () => void;
    onBlur?: () => void;
};

function useDatePicker({
    onChange,
    setValue,
    checkErrorVisibility,
    path,
    onFocus,
    onBlur,
    value,
    label,
    toolbarTitle,
    minDate,
    maxDate,
    disabled,
    filterInput,
    required,
    tabIndex,
    inputTextExtraClassName,
    startAdornment,
    autoFocus
}: HookParamsType): DatePickerHook {
    const [isOpen, setIsOpen] = React.useState<boolean>(false);
    const [dateString, setDateString] = React.useState<string>("");

    const [errors, setErrors] = React.useState<Array<string>>([]);

    const { formatDate, getMaskDate, getFormatDate } = useDateFormat();

    const mask = React.useMemo(() => getMaskDate(), [getMaskDate]);

    const momentFormat = React.useMemo(() => getFormatDate(), [getFormatDate]);

    const { onChange: onInputMaskChange } = useInputMask({
        setValue: setDateString,
        mask: mask,
    });

    const onOpen = React.useCallback(() => {
        setIsOpen(true);

        if (onFocus) {
            onFocus();
        }
    }, [onFocus]);

    const onClose = React.useCallback(() => {
        setIsOpen(false);

        if (onBlur) {
            onBlur();
        }
    }, [onBlur]);

    const onTodayIconClick = React.useCallback(() => {
        if (!disabled) {
            setIsOpen(true);
        }
    }, [disabled]);

    const onChangeCalculated = React.useCallback(
        (date: Moment | null) => {
            if (onChange) {
                onChange(date ? date.valueOf() : null);
            }

            if (setValue && path) {
                setValue(path, date ? date.valueOf() : null);
            }
        },
        [onChange, setValue, path]
    );

    const onInputTextChange = React.useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            onInputMaskChange(event);

            const tmpDate: string = event.target.value + "";

            if (moment(tmpDate, momentFormat, true).isValid()) {
                const momentDate = moment(tmpDate, momentFormat);

                if (minDate && momentDate.clone().startOf("day").isBefore(minDate.startOf("day"))) {
                    onChangeCalculated(null);
                } else if (
                    maxDate &&
                    momentDate.clone().endOf("day").isAfter(maxDate.endOf("day"))
                ) {
                    onChangeCalculated(null);
                } else {
                    onChangeCalculated(momentDate);
                }
            } else {
                onChangeCalculated(null);
            }
        },
        [onChangeCalculated, onInputMaskChange, minDate, maxDate, momentFormat]
    );

    const onDatePickerChange = React.useCallback(
        (date: Moment | null) => {
            setDateString(date ? formatDate(date.valueOf()) : "");

            onChangeCalculated(date);
        },
        [onChangeCalculated, formatDate]
    );

    React.useEffect(() => {
        const tmpErrors: Array<string> =
            checkErrorVisibility && path ? checkErrorVisibility(path) : [];

        if (dateString !== "" && tmpErrors && tmpErrors.length > 0) {
            setErrors(["Data non valida"]);
        } else {
            setErrors(tmpErrors);
        }
    }, [checkErrorVisibility, path, dateString]);

    const clearPicker = React.useCallback(() => {
        setDateString("");
    }, []);

    return React.useMemo(
        (): DatePickerHook => ({
            isOpen,
            errors,
            dateString,
            onOpen,
            onClose,
            onTodayIconClick,
            onInputTextChange,
            onDatePickerChange,
            clearPicker,
            onFocus,
            onBlur,
            value,
            label,
            toolbarTitle,
            minDate,
            maxDate,
            disabled,
            filterInput,
            required,
            tabIndex,
            inputTextExtraClassName,
            startAdornment,
            autoFocus
        }),
        [
            isOpen,
            errors,
            dateString,
            onOpen,
            onClose,
            onTodayIconClick,
            onInputTextChange,
            onDatePickerChange,
            clearPicker,
            onFocus,
            onBlur,
            value,
            label,
            toolbarTitle,
            minDate,
            maxDate,
            disabled,
            filterInput,
            required,
            tabIndex,
            inputTextExtraClassName,
            startAdornment,
            autoFocus
        ]
    );
}

export default useDatePicker;
